import { cloudStorage } from '@tma.js/sdk';

/**
 * Зеркало в Telegram CloudStorage — НЕ источник правды.
 *
 * Источник правды — localStorage: он синхронный и работает всегда.
 * Облако нужно ровно для одного: начал на телефоне, продолжил
 * на компьютере. Оно может быть недоступно (старый клиент, десктоп
 * без поддержки), и приложение обязано этого не заметить.
 *
 * Запись идёт с задержкой: тап-счётчик меняет состояние сотни раз
 * подряд, и слать столько же сетевых вызовов нельзя. Плюс сброс
 * при уходе со страницы — иначе последние изменения не доедут.
 */

const DEBOUNCE_MS = 2500;

const pending = new Map<string, string>();
let timer: ReturnType<typeof setTimeout> | null = null;

export function isCloudAvailable(): boolean {
  try {
    return cloudStorage.setItem.isAvailable();
  } catch {
    return false;
  }
}

function flush(): void {
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
  if (!pending.size || !isCloudAvailable()) {
    pending.clear();
    return;
  }

  const batch = [...pending.entries()];
  pending.clear();

  for (const [key, value] of batch) {
    // Значение в CloudStorage ограничено 4096 символами. Молча
    // обрезать нельзя — это испортит данные; лучше не отправить.
    if (value.length > 4096) {
      console.warn(`[SayfAzkari] ${key} не влезает в CloudStorage`, value.length);
      continue;
    }
    void cloudStorage.setItem(key, value).catch(() => {
      /* сеть или неподдерживаемый клиент — локальная копия уже есть */
    });
  }
}

export function cloudWrite(key: string, value: string): void {
  if (!isCloudAvailable()) return;
  pending.set(key, value);
  if (timer) clearTimeout(timer);
  timer = setTimeout(flush, DEBOUNCE_MS);
}

export async function cloudRead(keys: string[]): Promise<Record<string, string>> {
  if (!isCloudAvailable()) return {};
  try {
    return await cloudStorage.getItems(keys);
  } catch {
    return {};
  }
}

/** Свернули приложение — дописываем то, что не успело уйти */
export function installCloudFlush(): void {
  const onHide = () => {
    if (document.visibilityState === 'hidden') flush();
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', flush);
}
